/**
 * DI-3-S70 — the ONE per-image time the drop-time log check reads: a pure
 * twin of the gateway's `shared/intake-rules/gate-time.ts`
 * (`parseExifOffsetMs` / `exifOffsetMsOf` / `gateTimeMsOf`, DI-D18 c1 amended
 * 2026-10-21, story DI-TIME-01). Keep the two in step: the unit-test table
 * (`gateTime.test.ts`) is the BE's `di-time-01.exif-offset-gate.spec.ts` A1
 * table, case for case.
 *
 *   1. the file carries `drone-dji:UTCAtExposure` (DJI P1 / H30T / M4T)
 *        → UTCAtExposure − 18 s; the EXIF zone offset is IGNORED;
 *   2. else `capturedAtMs` (EXIF `DateTimeOriginal` + sub-seconds, read AS
 *      UTC — DI-D20 c1, stored raw) MINUS the camera's stated zone offset
 *      `OffsetTimeOriginal` (fallback `OffsetTime` only when
 *      `OffsetTimeOriginal` is absent), when it parses as `±HH:MM`;
 *      `+00:00` subtracts 0 — the F-M400 behaviour, byte-identical;
 *   3. no offset, or one that does not parse → `capturedAtMs` as it is.
 *
 * THIS IS THE GATE ONLY. The data points still send the RAW `capturedAtMs`
 * (`sync/toDataPointInput.ts`, DI-D20): the worker measures the per-flight
 * offset on it, so applying the zone there would count it twice. The set's
 * content id (`sets.ts:stableClusterId`) stays on the raw time too.
 */

/** GPS − UTC = 18 s since 2017-01-01 — the gateway's `GPS_UTC_LEAP_S` (`utc-at-exposure.ts`). */
export const GPS_UTC_LEAP_S = 18;

/** The widest zone offset that exists (UTC+14, Kiribati). Anything beyond is not a zone. */
export const EXIF_OFFSET_MAX_MIN = 14 * 60;

// EXIF 2.31 `OffsetTime*`: exactly "±HH:MM" (ASCII, 7 bytes with the NUL).
const EXIF_OFFSET_RE = /^([+-])(\d{2}):(\d{2})$/;

/**
 * `OffsetTimeOriginal` / `OffsetTime` → the signed offset in ms (`+01:00` →
 * +3 600 000), or `null` when the value is absent or not a `±HH:MM` zone
 * (minutes ≥ 60, more than 14 h, any other shape). Trailing NULs and
 * surrounding whitespace are ignored.
 */
export function parseExifOffsetMs(value: unknown): number | null {
  if (typeof value !== "string") return null;
  // eslint-disable-next-line no-control-regex -- the EXIF ASCII terminator, as the gateway strips it
  const m = EXIF_OFFSET_RE.exec(value.replace(/\u0000+$/, "").trim());
  if (!m) return null;
  const hours = Number(m[2]);
  const minutes = Number(m[3]);
  if (minutes >= 60) return null;
  const total = hours * 60 + minutes;
  if (total > EXIF_OFFSET_MAX_MIN) return null;
  return (m[1] === "-" ? -1 : 1) * total * 60_000;
}

/** The two EXIF zone tags of one image. */
export interface ExifOffsetTags {
  offsetTimeOriginal?: unknown;
  offsetTime?: unknown;
}

const present = (v: unknown): boolean =>
  v !== null && v !== undefined && !(typeof v === "string" && v.trim() === "");

/**
 * The image's stated zone offset, ms: `OffsetTimeOriginal`; `OffsetTime` only
 * when `OffsetTimeOriginal` is ABSENT. A present but unparsable
 * `OffsetTimeOriginal` is "no offset" — never silently replaced by the other tag.
 */
export function exifOffsetMsOf(
  tags: ExifOffsetTags | null | undefined,
): number | null {
  if (!tags) return null;
  if (present(tags.offsetTimeOriginal))
    return parseExifOffsetMs(tags.offsetTimeOriginal);
  return parseExifOffsetMs(tags.offsetTime);
}

/** `UTCAtExposure` (already parsed to ms) → UTC: only the leap seconds are corrected. */
export function leapCorrectedMs(
  utcAtExposureMs: number,
  leapS: number = GPS_UTC_LEAP_S,
): number {
  return utcAtExposureMs - leapS * 1000;
}

/**
 * THE gate time of one image, epoch ms, or `null` when it has no usable time.
 * `captureMs` = `capturedAtMs` (raw), `utcAtExposureMs` = the parsed
 * `UTCAtExposure`, `exifOffsetMs` = `exifOffsetMsOf(...)`.
 */
export function gateTimeMsOf(
  captureMs: number | null,
  utcAtExposureMs: number | null,
  exifOffsetMs: number | null,
): number | null {
  if (utcAtExposureMs !== null) return leapCorrectedMs(utcAtExposureMs);
  if (captureMs === null || !Number.isFinite(captureMs)) return null;
  return exifOffsetMs === null ? captureMs : captureMs - exifOffsetMs;
}
